import {IInfo, IInfoBroker, IInfoConsumer} from "/@lib@/commons/infos.js";
import {ILastDatasBuilder, JLastDatas} from "/@lib@/commons/lastDatas.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {BaseAreaView, OBaseAreaViewInit} from "/@lib@/commons/views.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {SRC, srcId, srcRef, srcUri} from "/@lib@/wsp/src.js";
import {IWspUiEnv, JWspUriChangeMsg, WSP, Wsp} from "/@lib@/wsp/wsp.js";
import {EWspChangesEvts, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {InfoCurrentItem, InfoFocusItem, InfoFocusNodeInItem, InfoReqCurrentItem} from "/@lib@/wsp/item.js";
import {SRCDRAWER, SrcPointer, SrcPointerDblClick} from "/@back@/wsp/widgets/srcDrawer.js";
import {TemplateResult} from "/@res@/litHtml/lib/template-result.js";
import {render} from "/@res@/litHtml/lit-html.js";
import {xhtml} from "/@lib@/commons/utils/htmlLit.js";
import {BASIS, PickInit} from "/@back@/commons/basis.js";
import {ACTION, Action} from "/@lib@/commons/actions.js";
import {BarActions, OBarActionsInit} from "/@back@/commons/widgets/bars.js";
import {OSrcHistoInit, SrcHisto} from "/@back@/wsp/widgets/srcHisto.js";


/**
 */
export interface Toc extends BaseAreaView<IRegPointer<IWspUiEnv>> {
	initialize(init: OTocInit): this
}

export interface OTocInit extends OBaseAreaViewInit<IRegPointer<IWspUiEnv>>,
	PickInit<Toc, 'infoBroker' | 'transformFacet'> {
	/** Masque l'entête avec le ptrItem. */
	hideHeader?: boolean

	/** nombre maximal de niveaux à déplier à l'init */
	maxLevelOpenedIntended?: number

	lastDatas?: JLDToc

	/** Personnalisation de la construction de la TOC. */
	tocItemTpl?: (this: Toc, tocItem: ITocItem, hideTitle: boolean, depthTitle: number, stackItems: ITocItem[]) => TemplateResult;

}

interface JLDToc {
	roots: srcRef[]
}

export class Toc extends BaseAreaView<IRegPointer<IWspUiEnv>> implements IInfoConsumer, ILastDatasBuilder {
	reg: IReg<IWspUiEnv>;
	wsp: Wsp;

	/** Réagira aux infos issues de ce broker (item courant) et dispatchera les demandes de focus / sélection. */
	infoBroker: IInfoBroker;

	/**
	 * Paramètres de la facette de transformation pour obtenir la toc
	 * Valeur par défaut : "facet=outline"
	 * Intégré ainsi dans la requête: `transform=facet&${transformFacet}`
	 */
	get transformFacet(): string {
		return this.config.transformFacet ? `transform=facet&${this.config.transformFacet}` : "transform=facet&facet=outline"
	}

	protected config: OTocInit;

	/* Map des Toc des items impliqués. Double indexation srcUri et srcId. */
	protected _srcMap: Map<srcUri & srcId, ITocItem>;

	protected _srcHisto?: SrcHisto;

	_rootSrc: SrcPointer;
	protected _view: HTMLElement;


	/** SrcUri actuellement "courrant" (InfoCurrentItem) pour le repérer dans la TOC. */
	protected _currentSrcUri: srcUri;

	setRootSrcRef(srcRef: srcRef) {
		this._rootSrc.setSrcRef(srcRef);
	}

	getRootSrcRef(): srcRef {
		return this._rootSrc.srcRef;
	}

	getRootSrcUri(): srcUri {
		const sd = this._rootSrc.shortDescs[0];
		return sd ? sd.srcUri : null;
	}

	/** Retourne true si le noeud de l'arbre doit être ouvert */
	protected isLevelOpened(tocItem: ITocItem, depth: number): boolean {
		return depth < this._maxLevelOpenedIntended;
	}

	protected _maxLevelOpenedIntended: number = 5

	isInToc(srcRef: srcRef): boolean {
		if (!this._srcMap) return false;
		return this._srcMap.has(srcRef);
	}

	async ensureRowVisible(srcUri: srcUri) {
		//TODO ensureRowVisible
	}

	protected _initialize(init: OTocInit) {
		super._initialize(init); //init this.reg, this.area, this.areaContext
		this.wsp = this.reg.env.wsp;
		this.infoBroker = init.infoBroker;
		this.config = init;

		if (init.maxLevelOpenedIntended) this._maxLevelOpenedIntended = init.maxLevelOpenedIntended;

		//Construction Ui
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin("wsp-toc", init);
		if (this.localName !== "wsp-toc") this.reg.installSkin(this.localName, sr);
		this.reg.installSkin('scroll/small', sr);

		this._rootSrc = new SrcPointer().initialize({
			reg: this.reg,
			place: this.reg.env.place,
			tpl: SRCDRAWER.itemCodeAloneTpl,
			defaultAction: this.reg.getSvc("ptrItemDblClick", SrcPointerDblClick.SINGLETON),
			sgnPattern: /.*#Map\b.*/,
			draggable: true,
			dropable: true,
			dispatchHighlight: true,
		});
		this._rootSrc.classList.add("fixedIconSize");

		if (!init.hideHeader) {
			//const ld = init.lastDatas?[this.getAttribute('last-datas')];
			this._srcHisto = <SrcHisto title="Historique des dernières racines sélectionnées" î={{
				reg: this.reg,
				icon: "/@skin@/wsp/views/toc/histo.svg",
				srcPointer: this._rootSrc,
				history: init.lastDatas?.roots
			} as OSrcHistoInit}/> as SrcHisto;
			sr.appendChild(<div id="headBar">
				<div id="rootSel">
					{this._rootSrc}
					{this._srcHisto}
				</div>
				<BarActions id="options" î={{
					actions: [new Action<Toc>()
						.setLabel("Déplier d'un niveau supplémentaire")
						.setIcon("/@skin@/commons/icons/expandMore.svg")
						.setExecute((ctx) => {
							ctx._maxLevelOpenedIntended++;
							ctx._render();
						}),
						new Action<Toc>()
							.setLabel("Tout replier")
							.setIcon("/@skin@/commons/icons/collaps.svg").setExecute((ctx) => {
							ctx._maxLevelOpenedIntended = 1;
							ctx._render();
						})],
					disableFullOverlay: true,
					uiContext: "bar",
					actionContext: this
				} as OBarActionsInit<Toc>}/>
			</div>);
		}

		this._rootSrc.onSrcRefChange.add(() => {this.rebuildFromRoot()});

		this._view = sr.appendChild(<div id="view"/>);
	}

	/** Personnalisation de la construction de la TOC. */
	protected tocItemTpl(this: Toc, tocItem: ITocItem, hideTitle: boolean, depthTitle: number, stackItems: ITocItem[]): TemplateResult {
		if (this.config.tocItemTpl)
			return this.config.tocItemTpl.call(this, tocItem, hideTitle, depthTitle, stackItems);
		//item en erreur
		if (tocItem.srcSt < 0) return xhtml`<div class="notFound"><wsp-toc-twisty p.state="${'notFound'}"/>Item non trouvé</div>`;
		//check cycles
		if (stackItems.indexOf(tocItem) >= 0) {
			return xhtml`<wsp-toc-title depth="${depthTitle}"><wsp-toc-twisty p.state="${'cycle'}" srcRef="${tocItem.srcId || tocItem.srcUri}"/>${tocItem.title || SRC.extractLeafFromUri(tocItem.srcUri)}</wsp-toc-title>`;
		}
		stackItems.push(tocItem);
		//Tpl des nodes
		const nodeState = tocItem.nodes.length ? (this.isLevelOpened(tocItem, depthTitle) ? "opened" : "closed") : 'leaf';
		const current = this._currentSrcUri === tocItem.srcUri;
		return xhtml`<wsp-toc-item p.tocItem="${tocItem}" srcRef="${tocItem.srcId || tocItem.srcUri}">
			<wsp-toc-side b.current="${current}"/>
			<div class="itemBody">
				${hideTitle ? undefined : xhtml`<wsp-toc-title depth="${depthTitle}" b.current="${current}"><wsp-toc-twisty p.state="${nodeState}"/>${tocItem.title || SRC.extractLeafFromUri(tocItem.srcUri)}</wsp-toc-title>`}
				<div class="children" b.hidden="${nodeState === "closed"}">
					${tocItem.nodes.map(node => this.tocNodeTpl(node, tocItem, hideTitle, depthTitle, stackItems))}
				</div>
			</div>
		</wsp-toc-item>`;
	}

	protected tocNodeTpl(tocNode: ITocNode, tocItem: ITocItem, hideTitle: boolean, depthTitle: number, stackItems: ITocItem[]): TemplateResult {
		try {
			depthTitle++;
			const defaultState = this.isLevelOpened(tocItem, depthTitle) ? "opened" : "closed";
			if (typeof tocNode.body === 'string') {
				const subTocItem = this._srcMap.get(tocNode.body);
				if (subTocItem) {
					return xhtml`<wsp-toc-node p.tocNode="${tocNode}">
						${tocNode.title ? xhtml`<wsp-toc-title depth="${depthTitle}"><wsp-toc-twisty p.state="${subTocItem.nodes.length ? defaultState : 'leaf'}"/>${tocNode.title || tocNode.name}</wsp-toc-title>` : undefined}
						${this.tocItemTpl(subTocItem, tocNode.title != null, depthTitle, stackItems)}
					</wsp-toc-node>`
				} else {
					//TODO tocItem pas encore chargé ou chargement en echec ?
					return xhtml`<div>...</div>`;
				}
			} else {
				return xhtml`<wsp-toc-node p.tocNode="${tocNode}">
					<wsp-toc-title depth="${depthTitle}"><wsp-toc-twisty p.state="${tocNode.body && tocNode.body.length ? defaultState : 'leaf'}"/>${tocNode.title || tocNode.name}</wsp-toc-title>
					<div class="children">
						${tocNode.body ? tocNode.body.map(node => this.tocNodeTpl(node, tocItem, hideTitle, depthTitle, stackItems)) : undefined}
					</div>
				</wsp-toc-node>`
			}
		} finally {
			depthTitle--;
		}
	}

	/** Appelé à l'init, au chgt de src root et en cas de perte de connexion. */
	protected async rebuildFromRoot() {
		this._srcMap = null;
		return this._fetchToc(this.getRootSrcRef());
	}

	protected async _fetchToc(srcRef: srcRef) {
		if (srcRef) {
			try {
				const txt = await WSP.fetchStreamText(this.wsp, this, srcRef, this._srcMap ? this.transformFacet + "&depth=1" : this.transformFacet);
				//console.log("_fetchToc::::", txt);
				const doc = DOM.parseDom(txt);
				if (!this._srcMap) this._srcMap = new Map();
				this.buildItemToc(doc.documentElement.firstElementChild);
			} catch (e) {
				this._srcMap = null;
				console.log(e);
			}
		} else {
			this._srcMap = null;
		}
		this._render();
	}

	/** Analyse un element DOM "item" pour enrichir this._srcMap. */
	protected buildItemToc(item: Element) {
		const srcUri = item.getAttribute("uri");
		const srcId = item.getAttribute("id");

		//cleanup
		let old = this._srcMap.get(srcUri);
		if (old && old.srcId && old.srcId !== srcId) this._srcMap.delete(old.srcId);
		old = this._srcMap.get(srcId);
		if (old && old.srcUri !== srcUri) this._srcMap.delete(old.srcUri);

		const root = DOM.findFirstChild(item, (n: Node): n is Element => n.nodeName === 'itemRoot');
		if (!root) return; //pas de <itemRoot> == présence d'un <cycle/>, on sort.
		const title = DOM.findFirstChild(root, (n: Node): n is Element => n.nodeName === 'titleNode');
		const tocItem = {
			srcUri: srcUri,
			srcId: srcId,
			srcSt: parseInt(item.getAttribute("st")),
			itSgn: item.getAttribute("sgn"),
			xpath: root ? root.getAttribute("xp").split('/') : null,
			title: title ? title.getAttribute("title") : null,
			nodes: []
		} as ITocItem;
		this._srcMap.set(srcUri, tocItem);
		if (srcId) this._srcMap.set(srcId, tocItem);
		const children = DOM.findFirstChild(root, (n: Node): n is Element => n.nodeName === 'children');
		if (!children) return;
		for (let ch = children.firstElementChild; ch; ch = ch.nextElementSibling) {
			if (ch.nodeName === "node") tocItem.nodes.push(this.buildNodeToc(ch));
		}
	}

	/** Analyse un element DOM nommé "node" et retourne un ITocNode. */
	protected buildNodeToc(node: Element): ITocNode {
		const title = DOM.findFirstChild(node, (n: Node): n is Element => n.nodeName === 'titleNode');
		const itNode = {
			xpath: node.getAttribute("xp").split('/'),
			name: node.getAttribute("name"),
			title: title ? title.getAttribute("title") : null
		} as ITocNode;
		const body = node.lastElementChild;
		if (body) {
			if (body.nodeName === "children") {
				const bodySet: ITocNode[] = itNode.body = [];
				for (let ch = body.firstElementChild; ch; ch = ch.nextElementSibling) {
					if (ch.nodeName === "node") bodySet.push(this.buildNodeToc(ch));
				}
			} else if (body.nodeName === "item") {
				const srcRef: srcRef = itNode.body = body.getAttribute("id") || body.getAttribute("uri");
				if (body.hasChildNodes() || body.getAttribute("st") === "-1") {
					//définition de cet item dans le flux.
					this.buildItemToc(body);
				} else {
					//Non présent dans le flux XML fetché
					if (!this._srcMap.has(srcRef)) {
						//srcUri non présente dans la map de cette toc, on doit charger la toc de cet item
						this._fetchToc(srcRef);
					}
				}
			}
		}
		return itNode;
	}

	_render() {
		const rootSrcUri = this.getRootSrcUri();
		if (!rootSrcUri) {
			render(this.noRootTpl(), this._view);
			return;
		}
		const tocItem = this._srcMap?.get(rootSrcUri);
		if (!tocItem) {
			render(this.noTocTpl(), this._view);
			return;
		}
		render(this.tocItemTpl(tocItem, false, 1, []), this._view);
	}


	noRootTpl(): TemplateResult {
		return xhtml`<c-msg level="info">Renseignez un item racine</c-msg>`;
	}

	noTocTpl(): TemplateResult {
		return xhtml`<c-msg level="info">Aucun plan pour cet item</c-msg>`;
	}

	/** Identifie l'item courant dans l'app. */
	_setCurrentItem(srcUri: srcUri | null) {
		this._currentSrcUri = srcUri;
		this._render();
		if (this._currentSrcUri) this.ensureRowVisible(this._currentSrcUri);
	}

	buildLastDatas?(parentLastDatas: JLastDatas): void {
		const ldKey = this.getAttribute('last-datas');
		if (ldKey) parentLastDatas[ldKey] = this._srcHisto?.setHistory<JLDToc>({} as JLDToc, "roots");
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoCurrentItem) {
			this._setCurrentItem(info.srcUri);
		}
	}

	onViewShown() {
		if (!this._onConnRenewed) {
			//1er init
			this._onConnRenewed = this.onConnRenewed.bind(this);
			this.reg.env.place.eventsMgr.on("onConnectionRenewed", this._onConnRenewed);
			this._wspUriChange = this.onWspUriChange.bind(this);
			this.reg.env.place.eventsMgr.on("wspUriChange", this._wspUriChange);
		}
		if (this.infoBroker) {
			this.infoBroker.addConsumer(this);
			const req = new InfoReqCurrentItem();
			this.infoBroker.dispatchInfo(req, this);
			this._setCurrentItem(req.srcUri);
			if (this._srcMap == null) {
				if (!this._rootSrc.srcRef && req.srcUri) {
					//On prend l'item courant par défaut.
					this._rootSrc.setSrcRef(req.srcUri, req.shortDesc);
				}
			}
		}
	}

	onViewHidden(closed?: boolean): void {
		if (this.infoBroker) this.infoBroker.removeConsumer(this);
		if (closed) {
			this.reg.env.place.eventsMgr.removeListener("onConnectionRenewed", this._onConnRenewed);
			this.reg.env.place.eventsMgr.removeListener("wspUriChange", this._wspUriChange);
		}
	}

	protected _wspUriChange: (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => void;

	protected onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') {
		if (!this._srcMap) return;
		if (msg.type === EWspChangesEvts.u) {
			const tocIt = this._srcMap.get(SRC.srcRef(msg));
			if (tocIt) this._fetchToc(SRC.srcRef(msg));
		} else if (msg.type === EWspChangesEvts.r) {
			const tocIt = this._srcMap.get(msg.srcUri);
			if (tocIt) {
				tocIt.srcSt = -1;
				tocIt.nodes = [];
				this._render();
			}
		}
	}

	protected _onConnRenewed: () => void;

	protected async onConnRenewed() {
		if (this.hidden) {
			if (this._srcMap) {
				//clear la view
				this._srcMap = null;
				this._render();
			}
		} else {
			this.rebuildFromRoot();
		}
	}
}

REG.reg.registerSkin("wsp-toc", 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	:focus-visible {
		outline: var(--focus-outline);
	}

	[hidden] {
		display: none !important;
	}

	#headTi {
		padding: 0 .3em;
		font-size: 80%;
		white-space: nowrap;
		color: var(--fade-color);
	}

	#headBar {
		display: flex;
		flex-wrap: wrap;
		min-height: 1.5em;
		min-width: 0;
		border-bottom: 1px solid var(--border-color);
		padding: .2em;
	}

	#rootSel {
		flex: 1 0 auto;
		display: flex;
		min-height: 1.5em;
		min-width: 0;
	}

	wsp-src-pointer {
		flex: 0 0 auto;
		min-width: 0;
		margin: 0;
	}

	#options {
		flex: 1 0 auto;
		/*background-color: var(--bgcolor);*/
		justify-content: flex-end;
	}

	#view {
		flex: 1;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		overflow: auto;
		padding: 4px 2px;
		background-color: var(--row-bgcolor);;
	}

	wsp-toc-item {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	wsp-toc-node {
		display: block;
	}

	wsp-toc-side {
		display: flex;
		min-height: 0;
		min-width: 0;
		width: .7em;
		border-inline-start: 1px solid #B68842;
		border-radius: .7em;
	}

	wsp-toc-side[current] {
		background: linear-gradient(90deg, #B68842, transparent .3em);
	}

	wsp-toc-side:not([current]):hover {
		/*background: linear-gradient(90deg, #B68842, transparent);*/
		filter: var(--hover-filter);
	}

	wsp-toc-node > wsp-toc-title,
	wsp-toc-node > .children {
		padding-inline-start: .7em; /* décalage en l'absence de wsp-toc-side */
	}

	wsp-toc-title {
		display: flex;
		min-height: 0;
		min-width: 0;
		cursor: pointer;
		user-select: none;
	}

	wsp-toc-title[depth='2'] {
		color: var(--alt1-color);
	}

	wsp-toc-title[depth='3'] {
		color: var(--alt2-color);
	}

	wsp-toc-title[depth='5'] {
		color: var(--alt1-color);
	}

	wsp-toc-title[depth='6'] {
		color: var(--alt2-color);
	}

	wsp-toc-twisty {
		padding-inline-start: 1em;
		background: url(/@skin@/wsp/views/toc/opened.svg) no-repeat center / .9em;
	}

	wsp-toc-twisty[state=closed] {
		background: url(/@skin@/wsp/views/toc/closed.svg) no-repeat center / .9em;
	}

	wsp-toc-twisty[state=leaf] {
		background: url(/@skin@/wsp/views/toc/leaf.svg) no-repeat center / .9em;
	}

	.notFound {
		color: var(--error-color);
		font-style: italic;
	}

	wsp-toc-twisty[state=notFound] {
		background: url(/@skin@/wsp/views/toc/notFound.svg) no-repeat center / .9em;
	}

	wsp-toc-twisty[state=cycle] {
		background: url(/@skin@/wsp/views/toc/cycle.svg) no-repeat center / .9em;
	}

	wsp-toc-twisty:not([state=notFound]):hover {
		filter: var(--hover-filter);
		cursor: auto;
	}
`);

customElements.define("wsp-toc", Toc);

export interface ITocItem {
	srcUri: srcUri
	srcId: srcId
	itSgn: string
	srcSt: number

	/**
	 * Title associé à la racine de l'item. Soit c'est le titre de la racine de la TOC, soit
	 * ce sera le titre des ITocNode dans les quels cet item est référencé (part/@sc:refUri avec des metas à la part).
	 */
	title?: string

	/** Fragments du xpath pointant le noeud parent des ITocNode fils. */
	xpath: string[]

	nodes: ITocNode[]
}

export interface ITocNode {

	/** XPath relatif à son contexte parent. */
	xpath: string[]

	/** Nom du modèle du node : utilisé si pas de title.*/
	name: string

	/**
	 * Titre du noeud. Si le contenu de ce noeud est externalisé (part/@sc:refUri), et que ce titre est renseigné,
	 * il s'agit du titre issu des metas de la part qui doit donc surchargé le titre du ITocItem pointé.
	 */
	title?: string

	/** Soit tout le body du node est porté par un autre item (part avec un ptrItem), soit il contient sa liste de fils. */
	body?: srcUri | ITocNode[]
}


export class WspTocItem extends HTMLElement {
	tocItem: ITocItem;

	buildXpath(xpath: string[]): srcRef {
		if (this.tocItem.xpath) xpath.push(...this.tocItem.xpath);
		return this.getAttribute("srcRef");
	}
}

customElements.define("wsp-toc-item", WspTocItem);


export class WspTocNode extends HTMLElement {
	tocNode: ITocNode;

	buildXpath(xpath: string[]): srcRef {
		const p = this.parentElement.closest("wsp-toc-node,wsp-toc-item") as WspTocNode | WspTocItem;
		const srcRef = p.buildXpath(xpath);
		if (this.tocNode.xpath) xpath.push(...this.tocNode.xpath);
		return srcRef;
	}
}

customElements.define("wsp-toc-node", WspTocNode);


export class WspTocTitle extends HTMLElement {
	constructor() {
		super();
		BASIS.makeClickable(this);
		this.onclick = this.onClick;
		this.onkeydown = this.onKeyDown;
	}

	onClick(ev: MouseEvent) {
		const p = this.parentElement.closest("wsp-toc-node,wsp-toc-item") as WspTocNode | WspTocItem;
		const xp = [] as string[];
		const srcRef = p.buildXpath(xp);
		const toc = DOMSH.findHost<Toc>(this);
		toc.infoBroker.dispatchInfo(new InfoFocusNodeInItem(xp.join("/"), srcRef), toc);
	}

	openFolder(): boolean {
		const twisty = this.querySelector("wsp-toc-twisty") as WspTocTwisty;
		if (twisty && twisty.state === "closed") {
			twisty.state = "opened";
			return true;
		}
		return false;
	}

	closeFolder(): boolean {
		const twisty = this.querySelector("wsp-toc-twisty") as WspTocTwisty;
		if (twisty && twisty.state === "opened") {
			twisty.state = "closed";
			return true;
		}
		return false;
	}

	toggleFolderOrGotoCycle(): boolean {
		const twisty = this.querySelector("wsp-toc-twisty") as WspTocTwisty;
		if (twisty) {
			const st = twisty.state;
			if (st === "closed") {
				twisty.state = "opened";
				return true;
			} else if (st === "opened") {
				twisty.state = "closed";
				return true;
			} else if (st === "cycle") {
				twisty.gotoCycle();
				return true;
			}
		}
		return false;
	}

	ensureVisible() {
		this.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'smooth'});
	}

	onKeyDown(ev: KeyboardEvent) {
		let n;
		switch (ev.key) {
		case 'ArrowDown' :
			n = DOM.findNext(this, null, IS_WspTocTitle);
			if (n) n.focus();
			break;
		case 'ArrowUp' :
			n = DOM.findPrevious(this, null, IS_WspTocTitle);
			if (n) n.focus();
			break;
		case 'ArrowLeft' :
			if (window.getComputedStyle(this).direction === 'rtl') {
				if (!this.openFolder()) {
					this.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowDown'}));
					return;
				}
			} else {
				if (!this.closeFolder()) {
					this.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowUp'}));
					return;
				}
			}
			this.ensureVisible();
			break;
		case 'ArrowRight' :
			if (window.getComputedStyle(this).direction === 'rtl') {
				if (!this.closeFolder()) {
					this.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowUp'}));
					return;
				}
			} else {
				if (!this.openFolder()) {
					this.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowDown'}));
					return;
				}
			}
			this.ensureVisible();
			break;
		case 'Enter' :
			if (ACTION.isAccelPressed(ev)) {
				if (!this.toggleFolderOrGotoCycle()) return;
				this.ensureVisible();
				break;
			}
		default :
			//	console.log("evt keydown:::"+evt.keyCode);
			return;
		}
		ev.stopImmediatePropagation();
		ev.preventDefault();
	}

}

function IS_WspTocTitle(n: Node): n is WspTocTitle {return n instanceof WspTocTitle}

customElements.define("wsp-toc-title", WspTocTitle);

export class WspTocTwisty extends HTMLElement {
	constructor() {
		super();
		this.onpointerdown = this.onPointerDown;
		this.onclick = this.onClick;
	}

	get state(): 'leaf' | 'opened' | 'closed' | 'notFound' | 'cycle' {return this.getAttribute('state') as any}

	set state(st: 'leaf' | 'opened' | 'closed' | 'notFound' | 'cycle') {
		switch (st) {
		case 'opened': {
			const chList = this.getChildrenList();
			if (chList) DOM.setHidden(chList, false);
			break;
		}
		case 'closed': {
			const chList = this.getChildrenList();
			if (chList) DOM.setHidden(chList, true);
			break;
		}
		case 'leaf' :
		case 'notFound':
		case 'cycle': {
			break;
		}
		default:
			throw Error("State unknown: " + st);
		}
		this.setAttribute('state', st);
	}

	gotoCycle(): boolean {
		if (this.state === "cycle") {
			const parent = DOM.findParent(this, null, (n): n is HTMLElement => n instanceof WspTocItem && n.getAttribute('srcRef') === this.getAttribute('srcRef'));
			if (parent) {
				const title = parent.querySelector<HTMLElement>('wsp-toc-title');
				if (title) {
					title.focus();
					return true;
				}
			}
		}
		return false;
	}

	getChildrenList(): HTMLElement {
		const parent = DOM.findParent(this, null, (n): n is HTMLElement => n instanceof WspTocNode || (n instanceof HTMLElement && n.classList.contains("itemBody")));
		return DOM.findLastChild(parent, (n): n is HTMLElement => n instanceof HTMLElement && n.classList.contains("children"));
	}

	onPointerDown(ev: PointerEvent) {
		const st = this.state;
		if (st === "leaf") return;
		if (st === "cycle") {
			if (this.gotoCycle()) {
				ev.stopImmediatePropagation();
				ev.preventDefault(); //bloque la prise de focus sur notre propre wsp-toc-title.
			}
			return;
		}
		ev.stopImmediatePropagation();
		this.state = st === "closed" ? "opened" : "closed";
	}

	onClick(ev: PointerEvent) {
		ev.stopImmediatePropagation(); //bloque le gotoFocus dans les wed.
	}
}

customElements.define("wsp-toc-twisty", WspTocTwisty);

/**
 * Barre latérale représentant un item.
 * Note accessibilité : pour une naviegation standard dans l'arbre des titres du plan, on a choisit de ne pas rendre focusbale les WspTocSide.
 */
export class WspTocSide extends HTMLElement {
	constructor() {
		super();
		this.onclick = this.onClick;
	}

	onClick(ev: MouseEvent) {
		const toc = DOMSH.findHost<Toc>(this);
		toc.infoBroker.dispatchInfo(new InfoFocusItem(this.parentElement.getAttribute("srcRef")), toc);
	}
}

customElements.define("wsp-toc-side", WspTocSide);
